import { collectionGroup, getDocs, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { membershipCardSchema, type MembershipCard } from "./schemas";

export type MembershipsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; memberships: MembershipCard[] };

/**
 * Collection-group query: `members` where `uid == me` (P16).
 *
 * Requires, and both now exist:
 * - the collection-group rule on `{path=**}/members`, which is what forces the
 *   uid filter to be part of the query rather than applied after the read
 * - the collection-group index `members(uid ASC)`, declared in
 *   infrastructure/gcp/terraform/firestore.tf
 */
/** The memberships that parse as cards; one that does not is dropped. */
export async function loadMemberships(uid: string): Promise<MembershipCard[]> {
  const snap = await getDocs(
    query(collectionGroup(db, "members"), where("uid", "==", uid)),
  );
  const memberships: MembershipCard[] = [];
  snap.forEach((d) => {
    const result = membershipCardSchema.safeParse(d.data());
    if (result.success) memberships.push(result.data);
  });
  return memberships;
}

export function useMyMemberships(uid: string): MembershipsState {
  const [state, setState] = useState<MembershipsState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const memberships = await loadMemberships(uid);
        if (cancelled) return;
        setState({ status: "success", memberships });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            err instanceof Error ? err.message : "Failed to load your events",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  return state;
}
